import { createI18n } from '@shkola/i18n';
import { OfflineProvider, createAppQueryClient, registerOfflineActions } from '@shkola/api';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { AuthProvider } from './auth/AuthProvider';
import { DemoLink } from './auth/DemoLink';
import { Card, CenteredScreen } from './components/ui';
import { readEnv } from './env';
import { encryptedWebStorage } from './lib/encrypted-storage';
import { createSupabase } from './lib/supabase';
import { router } from './router';
import '@fontsource/caveat/600.css';
import '@fontsource/caveat/700.css';
// «Форми»: Onest (OFL, Simpals), the light grotesk of its headings
import '@fontsource-variable/onest';
import './styles.css';

declare global {
  interface Window {
    EXCALIDRAW_ASSET_PATH?: string | string[];
  }
}
// The board's fonts come from our own site (vite.config.ts). Excalidraw reads this when its code
// loads, so it is set here, before any board is opened.
window.EXCALIDRAW_ASSET_PATH = '/excalidraw/';

const env = readEnv(import.meta.env as Record<string, string | undefined>);
const i18n = await createI18n([initReactI18next]);
const root = createRoot(document.getElementById('root') as HTMLElement);

if (!env.supabaseUrl || !env.supabaseKey) {
  // not connected to a backend yet — explain instead of failing silently
  root.render(
    <StrictMode>
      <CenteredScreen>
        <Card>
          <h1 className="mb-2 text-xl font-semibold">{i18n.t('setup.title')}</h1>
          <p className="text-sm text-text-muted">{i18n.t('setup.body')}</p>
        </Card>
      </CenteredScreen>
    </StrictMode>,
  );
} else {
  const supabase = createSupabase(env.supabaseUrl, env.supabaseKey);
  const queryClient = createAppQueryClient();
  registerOfflineActions(queryClient, supabase);
  root.render(
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <OfflineProvider client={queryClient} storage={encryptedWebStorage}>
          <AuthProvider supabase={supabase}>
            <DemoLink />
            <RouterProvider router={router} />
          </AuthProvider>
        </OfflineProvider>
      </I18nextProvider>
    </StrictMode>,
  );
}
